import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SummaryStripModuleObject } from '../../constants/constant';
import {
  Header,
  ITableData,
  TypeOfTableData
} from '../../pricing-simulator/types/generic-table.model';
import { filterColumnsForToggleView } from '../../pricing-simulator/utils/column-utils';
import { getScenarioTableData } from '../../scenario-manager/api/scenario-manager-api';
import fetchScenarioCompMetricsBarChart from '../api/scenario-comparison-api';
import { handleCurrencyToggleState } from '../../simulation-summary/store/simulation-summary-slice';

interface IScenarioComparisonState {
  metricToggle: boolean;
  fetchingTableData: boolean;
  fetchingScenarioCompMetricsBarCharts: boolean;
  // It will contain the details of created scenario
  scenarioManagerTableData?: ITableData;
  inputCols: Header[];
  selectedCompareScenariosList: any;
  kpisMinMaxMap: object;
  toggleData: [];
  isScenarioManagerToComparison: boolean;
  isTargetEnabled: boolean;
}

const initialState: IScenarioComparisonState = {
  metricToggle: false,
  fetchingTableData: false,
  fetchingScenarioCompMetricsBarCharts: false,
  inputCols: [],
  scenarioManagerTableData: {
    legend: { metadata: [] },
    ownKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    retailerKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    metadata: [],
    show_edited: { enabled: false, label: '' },
    row: [],
    toggle: {
      metadata: [],
      data: {}
    },
    output_toggle: {
      metadata: [],
      data: {}
    },
    summary_strip: {
      metadata: [],
      data: {
        internal_dt: 0,
        external_dt: 0,
        renovation: SummaryStripModuleObject,
        pricing: SummaryStripModuleObject,
        delist: SummaryStripModuleObject,
        distribution: SummaryStripModuleObject,
        innovation_table: SummaryStripModuleObject
      }
    },
    footer: {
      metadata: {
        enabled: false,
        label: ''
      },
      row: []
    },
    header: []
  },
  selectedCompareScenariosList: [],
  kpisMinMaxMap: {},
  toggleData: [],
  isScenarioManagerToComparison: false,
  isTargetEnabled: false
};

/**
 * Api to get the list of created scenarios
 */
const getListOfScenarios = createAsyncThunk(
  'getListOfScenarios',
  async ({
    filterData,
    sourceId
  }: {
    filterData: { filter_inputs: any[] };
    sourceId: number;
  }) => {
    const response = await getScenarioTableData(filterData, sourceId);
    return response;
  }
);

const getScenarioCompMetricsBarCharts = createAsyncThunk(
  'getScenarioCompMetricsBarCharts',
  async ({ sourceId, scenariosWithOrderOfSelection, isTargetEnabled }:
    { sourceId: number; scenariosWithOrderOfSelection: any, isTargetEnabled: boolean }) => {
    const response = await fetchScenarioCompMetricsBarChart(
      sourceId,
      scenariosWithOrderOfSelection,
      isTargetEnabled
    );
    return response;
  }
);

const scenarioComparisonSlice = createSlice({
  name: 'scenario-comparison',
  initialState,
  reducers: {
    handleMetricToggleChange(
      state,
      action: PayloadAction<{
        metricToggle: boolean;
      }>
    ) {
      state.metricToggle = action.payload.metricToggle;
    },
    handleRemoveScenarioSelection(
      state,
      action: PayloadAction<{
        updatedList: any;
      }>
    ) {
      state.selectedCompareScenariosList = action.payload.updatedList;
    },
    emptySelectedCompareScenariosList: (state) => {
      state.selectedCompareScenariosList = [];
    },
    handleScenarioManagerToComparison: (state, action: PayloadAction<{ isScenarioManagerToComparison: boolean; }>) => {
      state.isScenarioManagerToComparison = action.payload.isScenarioManagerToComparison;
    },
    handleCurrencyToggleStateComparison(
      state,
      action: PayloadAction<boolean>) {
      state.isTargetEnabled = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getListOfScenarios.pending, (state) => {
        state.fetchingTableData = true;
      })
      .addCase(getListOfScenarios.fulfilled, (state, action) => {
        state.fetchingTableData = false;
        state.scenarioManagerTableData = {
          ...action.payload
        };
        state.inputCols = filterColumnsForToggleView(
          [TypeOfTableData.DISPLAY, TypeOfTableData.INPUT],
          action.payload.header,
          ''
        );
      })
      .addCase(getListOfScenarios.rejected, (state) => {
        state.fetchingTableData = false;
      })
      .addCase(getScenarioCompMetricsBarCharts.pending, (state) => {
        state.fetchingScenarioCompMetricsBarCharts = true;
      })
      .addCase(getScenarioCompMetricsBarCharts.fulfilled, (state, action) => {
        state.fetchingScenarioCompMetricsBarCharts = false;
        state.selectedCompareScenariosList = action.payload.scenarios;
        state.kpisMinMaxMap = action.payload.metrics_values;
        state.toggleData = action.payload.toggle || [];
      })
      .addCase(getScenarioCompMetricsBarCharts.rejected, (state) => {
        state.fetchingScenarioCompMetricsBarCharts = false;
      })
});

export const {
  handleMetricToggleChange,
  handleRemoveScenarioSelection,
  emptySelectedCompareScenariosList,
  handleScenarioManagerToComparison,
  handleCurrencyToggleStateComparison
} = scenarioComparisonSlice.actions;

export { getListOfScenarios, getScenarioCompMetricsBarCharts };

export default scenarioComparisonSlice;
